/**
 * WhatsAppPendingBoard — admin recovery de envíos WhatsApp sin resolver
 * (WACO Fase 1.2).
 *
 * Superficie pequeña y operativa: lista los envíos en estado
 * accepted_pending_provider_id / unknown (los que bloquean el gate del
 * lead) y ofrece DOS resoluciones manuales, explícitas y auditadas:
 *   - "Marcar como fallido": evidencia externa de que NO salió
 *     (motivo obligatorio, código de error externo opcional).
 *   - "Confirmar como enviado": evidencia inequívoca de que SÍ salió,
 *     con el Meta wamid en mano (wamid + motivo obligatorios).
 * Sin reenviar, sin retry, sin liberar sin motivo — por decisión de
 * producto. El backend re-valida todo (roles, estado, wamid) y nunca
 * llama al provider; un 409 significa que el webhook u otro admin
 * resolvió antes: se refresca la lista, jamás se sobrescribe.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getWhatsAppPendingSends, markWhatsAppPendingFailed, markWhatsAppPendingSent,
} from "@/lib/whatsappApi";
import { Section } from "@/pages/Shell";
import {
  ArrowsClockwise, CaretDown, CaretUp, CheckCircle, WarningCircle, WhatsappLogo,
} from "@phosphor-icons/react";

function fmtAge(mins) {
  if (mins == null) return "—";
  if (mins < 60) return `hace ${mins} min`;
  if (mins < 48 * 60) return `hace ${Math.floor(mins / 60)} h`;
  return `hace ${Math.floor(mins / (24 * 60))} d`;
}

const STATUS_LABEL = {
  accepted_pending_provider_id: "Aceptado · esperando wamid",
  unknown: "Resultado incierto",
};

// Un nombre de fichero con forma de URL no se pinta: el backend ya manda
// la vista saneada, pero esta lista no enseña enlaces aunque le lleguen.
const safeText = (s) => (typeof s === "string" && s && !/:\/\//.test(s) ? s : null);

/** Qué se intentó enviar. Del documento solo nombre, tipo y versión. */
function describeKind(row) {
  if (row.kind === "template") return `Plantilla · ${row.template_name || "—"}`;
  if (row.kind === "document") {
    const doc = row.document || {};
    const tipo = safeText(doc.asset_kind);
    const version = doc.version != null && doc.version !== "" ? `v${doc.version}` : null;
    const detalle = [tipo, version].filter(Boolean).join(" ");
    return `Documento · ${safeText(doc.filename) || "—"}${detalle ? ` · ${detalle}` : ""}`;
  }
  return "Texto libre";
}

export default function WhatsAppPendingBoard() {
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [openId, setOpenId] = useState(null);
  const [action, setAction] = useState(null); // {id, type:'fail'|'sent'}
  const [reason, setReason] = useState("");
  const [extCode, setExtCode] = useState("");
  const [wamid, setWamid] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null); // {tone:'ok'|'error', text}

  const load = useCallback(async () => {
    try {
      const { items: rows } = await getWhatsAppPendingSends();
      setItems(rows);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForms = () => {
    setAction(null); setReason(""); setExtCode(""); setWamid("");
  };

  const toggleRow = (id) => {
    setOpenId((cur) => (cur === id ? null : id));
    resetForms();
    setNotice(null);
  };

  const submit = async (row) => {
    if (submitting) return;
    setSubmitting(true);
    setNotice(null);
    try {
      if (action?.type === "fail") {
        await markWhatsAppPendingFailed(row.send_request_id, {
          reason: reason.trim(), externalErrorCode: extCode.trim() || null,
        });
        setNotice({ tone: "ok", text: "Envío marcado como fallido. El lead vuelve a admitir envíos." });
      } else {
        await markWhatsAppPendingSent(row.send_request_id, {
          providerMessageId: wamid.trim(), reason: reason.trim(),
        });
        setNotice({ tone: "ok", text: "Envío confirmado como enviado. El mensaje ya aparece en la conversación." });
      }
      resetForms();
      setOpenId(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "whatsapp_pending_stale_state") {
        setNotice({ tone: "error", text: "Este envío ya fue resuelto (webhook u otro admin). Lista actualizada." });
        resetForms();
        setOpenId(null);
        await load();
      } else if (code === "whatsapp_pending_wamid_in_use") {
        setNotice({ tone: "error", text: "Ese wamid ya pertenece a otro mensaje. Revisa la evidencia." });
      } else if (typeof detail === "string" && detail) {
        setNotice({ tone: "error", text: detail });
      } else {
        setNotice({ tone: "error", text: "No se pudo aplicar la resolución." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const failReady = reason.trim().length >= 3;
  const sentReady = reason.trim().length >= 3 && wamid.trim().startsWith("wamid.");

  return (
    <Section
      title={
        <span className="inline-flex items-center gap-2">
          <WhatsappLogo size={18} weight="bold" className="text-emerald-600" />
          Envíos WhatsApp pendientes
          {phase === "ready" && (
            <span className="text-xs font-normal text-stone-500" data-testid="wa-pending-count">
              {items.length} sin resolver
            </span>
          )}
        </span>
      }
      action={
        <button type="button" onClick={load} className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1" data-testid="wa-pending-refresh">
          <ArrowsClockwise size={13} /> Actualizar
        </button>
      }
    >
      <p className="text-xs text-stone-500 dark:text-stone-400 mb-3">
        Envíos aceptados por WACO cuyo wamid de Meta nunca llegó (o con
        resultado incierto). Bloquean nuevos envíos a su lead hasta que un
        admin los resuelva con evidencia externa. Ninguna acción reenvía nada.
      </p>

      {notice && (
        <div
          className={`text-xs rounded px-3 py-2 border mb-3 ${
            notice.tone === "ok"
              ? "text-emerald-800 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800"
              : "text-red-700 bg-red-50 border-red-200 dark:text-red-300 dark:bg-red-950/40 dark:border-red-800"
          }`}
          data-testid="wa-pending-notice"
        >
          {notice.text}
        </div>
      )}

      {phase === "loading" && (
        <div className="dv-card p-6 text-center text-sm text-stone-400 animate-pulse">Cargando…</div>
      )}
      {phase === "error" && (
        <div className="dv-card p-6 text-center text-sm text-stone-500">
          No se pudo cargar la lista.{" "}
          <button type="button" onClick={load} className="underline">Reintentar</button>
        </div>
      )}
      {phase === "ready" && items.length === 0 && (
        <div className="dv-card p-6 text-center text-sm text-stone-400" data-testid="wa-pending-empty">
          <CheckCircle size={22} className="mx-auto mb-1 text-emerald-600" />
          No hay envíos pendientes de resolución.
        </div>
      )}

      {phase === "ready" && items.length > 0 && (
        <div className="dv-card divide-y divide-stone-100 dark:divide-stone-800 overflow-hidden">
          {items.map((row) => {
            const open = openId === row.send_request_id;
            return (
              <div key={row.send_request_id} data-testid={`wa-pending-${row.send_request_id}`}>
                <button
                  type="button"
                  onClick={() => toggleRow(row.send_request_id)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-stone-50 dark:hover:bg-stone-900"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-stone-800 dark:text-stone-100 truncate">
                      {row.lead_name || row.lead_id}
                      <span className="ml-2 text-xs font-normal text-stone-400">{row.recipient_masked}</span>
                    </div>
                    <div className="text-[11px] text-stone-500 dark:text-stone-400 break-words"
                         data-testid={`wa-pending-${row.send_request_id}-kind`}>
                      {describeKind(row)}
                      {" · "}{fmtAge(row.age_minutes)}
                      {" · "}{STATUS_LABEL[row.status] || row.status}
                    </div>
                  </div>
                  {open ? <CaretUp size={13} /> : <CaretDown size={13} />}
                </button>

                {open && (
                  <div className="px-4 pb-4 space-y-3 bg-stone-50/60 dark:bg-stone-900/40">
                    <div className="text-[11px] text-stone-500 pt-2">
                      <Link
                        to={`/leads/${row.lead_id}`}
                        className="underline hover:text-stone-800 dark:hover:text-stone-200"
                        data-testid={`wa-pending-${row.send_request_id}-lead-link`}
                      >
                        Ver ficha del lead
                      </Link>
                    </div>

                    {!action && (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => { setAction({ id: row.send_request_id, type: "fail" }); setNotice(null); }}
                          className="dv-btn dv-btn-ghost text-xs"
                          data-testid={`wa-pending-${row.send_request_id}-fail-open`}
                        >
                          Marcar como fallido
                        </button>
                        <button
                          type="button"
                          onClick={() => { setAction({ id: row.send_request_id, type: "sent" }); setNotice(null); }}
                          className="dv-btn dv-btn-ghost text-xs"
                          data-testid={`wa-pending-${row.send_request_id}-sent-open`}
                        >
                          Confirmar como enviado
                        </button>
                      </div>
                    )}

                    {action?.id === row.send_request_id && action.type === "fail" && (
                      <div className="space-y-2" data-testid={`wa-pending-${row.send_request_id}-fail-form`}>
                        <div className="text-xs text-stone-600 dark:text-stone-300">
                          Solo con evidencia externa de que el mensaje <strong>no</strong> salió
                          (p. ej. error confirmado por Meta/WACO). Libera el envío para este lead.
                        </div>
                        <label className="block text-[11px] text-stone-500">
                          Motivo (obligatorio)
                          <input type="text" className="dv-input mt-0.5 w-full text-sm" value={reason}
                                 maxLength={500} onChange={(e) => setReason(e.target.value)}
                                 data-testid="wa-pending-fail-reason" />
                        </label>
                        <label className="block text-[11px] text-stone-500">
                          Código de error externo (opcional, p. ej. 131042)
                          <input type="text" className="dv-input mt-0.5 w-full text-sm" value={extCode}
                                 maxLength={50} onChange={(e) => setExtCode(e.target.value)}
                                 data-testid="wa-pending-fail-code" />
                        </label>
                        <div className="flex gap-2">
                          <button type="button" disabled={!failReady || submitting}
                                  onClick={() => submit(row)}
                                  className="dv-btn dv-btn-primary text-xs disabled:opacity-50"
                                  data-testid="wa-pending-fail-confirm">
                            {submitting ? "Aplicando…" : "Confirmar fallo"}
                          </button>
                          <button type="button" onClick={resetForms} className="dv-btn dv-btn-ghost text-xs">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {action?.id === row.send_request_id && action.type === "sent" && (
                      <div className="space-y-2" data-testid={`wa-pending-${row.send_request_id}-sent-form`}>
                        <div className="text-xs rounded px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-800 flex items-start gap-1.5">
                          <WarningCircle size={14} className="shrink-0 mt-0.5" />
                          <span>
                            Uso excepcional: solo con evidencia inequívoca de que el mensaje
                            SÍ salió y conociendo el <strong>Meta message ID (wamid)</strong>.
                            El ID de conversación de WACO no sirve.
                          </span>
                        </div>
                        <label className="block text-[11px] text-stone-500">
                          Meta message ID (wamid.…)
                          <input type="text" className="dv-input mt-0.5 w-full text-sm font-mono" value={wamid}
                                 maxLength={256} onChange={(e) => setWamid(e.target.value)}
                                 placeholder="wamid.HBg…"
                                 data-testid="wa-pending-sent-wamid" />
                        </label>
                        <label className="block text-[11px] text-stone-500">
                          Motivo / evidencia (obligatorio)
                          <input type="text" className="dv-input mt-0.5 w-full text-sm" value={reason}
                                 maxLength={500} onChange={(e) => setReason(e.target.value)}
                                 data-testid="wa-pending-sent-reason" />
                        </label>
                        <div className="flex gap-2">
                          <button type="button" disabled={!sentReady || submitting}
                                  onClick={() => submit(row)}
                                  className="dv-btn dv-btn-primary text-xs disabled:opacity-50"
                                  data-testid="wa-pending-sent-confirm">
                            {submitting ? "Aplicando…" : "Confirmar como enviado"}
                          </button>
                          <button type="button" onClick={resetForms} className="dv-btn dv-btn-ghost text-xs">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
